import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { setSessionToken, clearSessionToken } from "@/utils/auth";
// DISABLED (multi-account): upsertAccountMeta, isAddAccountMode,
// clearAddAccountMode, storeUserToken
// DISABLED (multi-account): authAxios + getEnvVar fetched /users/me here only
// to cache account metadata for the switcher.
import { useToast } from "@/hooks/useToast";
import { getRedirectRoute } from "@/utils/onboardingConfig";

import { AuthTransition } from "../components/AuthTransition";
import {
  AUTH_FAILED,
  LINKEDIN_USER_EXISTS,
  LINKEDIN_NO_EMAIL,
  LINKEDIN_TOKEN_FAILED,
  LINKEDIN_USERINFO_FAILED,
  LINKEDIN_SESSION_EXPIRED,
  LINKEDIN_NOT_CONFIGURED,
  LINKEDIN_CANCELLED,
  LINKEDIN_GENERIC_ERROR,
} from "@/utils/toastMessages";

/**
 * Landing page after the backend processes the LinkedIn OAuth callback.
 *
 * The backend redirects here as:
 *   /auth/linkedin/success?access_token=<jwt>&onboarding_step=<step>
 *
 * On success  → redirect straight to the correct onboarding step.
 * On error    → show a toast and send the user back to /login or /signup based on intent.
 *
 * There is no success card and no 2.2-second hold behind a depleting progress
 * bar any more. Shares AuthTransition with the two email landing pages so all
 * three look identical — a spinner, then the destination.
 */
export const LinkedInCallbackPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  // Set once the token is accepted; the effect below navigates on the next
  // tick, so the card paints without anything waiting on it.
  const [result, setResult] = useState<{ intent: "signup" | "login"; route: string } | null>(null);

  useEffect(() => {
    const run = async () => {
      const accessToken = searchParams.get("access_token");
      const onboardingStep = searchParams.get("onboarding_step");
      const linkedinError = searchParams.get("linkedin_error");
      const intent = (searchParams.get("intent") ||
        sessionStorage.getItem("linkedin_oauth_intent") ||
        "signup") as "signup" | "login";
      const errorFallback = intent === "login" ? "/login" : "/signup";

      if (linkedinError) {
        let message: string;
        let redirectTo: string = errorFallback;

        switch (linkedinError) {
          case "user_already_exists":
            message = LINKEDIN_USER_EXISTS;
            redirectTo = "/login";
            break;
          case "user_not_found":
            message = "User not found";
            break;
          case "no_email":
            message = LINKEDIN_NO_EMAIL;
            break;
          case "token_exchange_failed":
            message = LINKEDIN_TOKEN_FAILED;
            break;
          case "userinfo_failed":
            message = LINKEDIN_USERINFO_FAILED;
            break;
          case "invalid_state":
          case "missing_params":
            message = LINKEDIN_SESSION_EXPIRED;
            break;
          case "not_configured":
            message = LINKEDIN_NOT_CONFIGURED;
            break;
          case "user_cancelled_login":
          case "access_denied":
            message = LINKEDIN_CANCELLED;
            break;
          default:
            message = LINKEDIN_GENERIC_ERROR;
        }

        showToast({ type: "error", message, duration: 4000 });
        navigate(redirectTo);
        return;
      }

      if (!accessToken) {
        showToast({
          type: "error",
          message: AUTH_FAILED,
          duration: 4000,
        });
        navigate(errorFallback);
        return;
      }

      // Drop any previous session before adopting the LinkedIn one. Previously
      // this was skipped in add-account mode so the opener's session survived;
      // with a single shared cookie the new login always replaces the old.
      clearSessionToken();

      // Activate the token as the session (shared cookie, all tabs).
      setSessionToken(accessToken);

      // DISABLED (multi-account): fetched /users/me here purely to cache the
      // account's display metadata and write its per-user token cookie.
      // AuthContext loads the user from the session token on the next render.
      //
      // try {
      //   const API_BASE = getEnvVar("API_BASE");
      //   const resp = await authAxios.get(`${API_BASE}/users/me`, {
      //     headers: { Authorization: `Bearer ${accessToken}` },
      //   });
      //   const d = resp.data;
      //   upsertAccountMeta({
      //     userId: d.id,
      //     email: d.email,
      //     fullName: d.fullName,
      //     role: d.role,
      //     roles: d.roles && d.roles.length > 0 ? d.roles : [d.role],
      //     profilePictureUrl: null,
      //   });
      //   storeUserToken(d.id, accessToken);
      // } catch {
      //   try {
      //     const payload = JSON.parse(atob(accessToken.split(".")[1]));
      //     const sub = payload?.sub ? String(payload.sub) : "";
      //     if (sub) storeUserToken(sub, accessToken);
      //   } catch {
      //     // If user-fetch fails, proceed anyway — AuthContext will fill details on next load.
      //   }
      // }

      sessionStorage.removeItem("linkedin_oauth_nonce");
      sessionStorage.removeItem("linkedin_oauth_intent");

      // DISABLED (multi-account): an add-account tab landed here after the
      // backend linked both accounts, broadcast "account_added" to refresh the
      // opener's switcher, and stopped at /home instead of onboarding.
      //
      // if (addAccountMode) {
      //   clearAddAccountMode();
      //
      //   if (typeof BroadcastChannel !== "undefined") {
      //     const ch = new BroadcastChannel("fwd_account_added");
      //     ch.postMessage({ type: "account_added" });
      //     ch.close();
      //   }
      //   navigate("/home", { replace: true });
      //   return;
      // }

      const redirectTo = onboardingStep
        ? getRedirectRoute(onboardingStep as Parameters<typeof getRedirectRoute>[0])
        : "/signup/2";

      setResult({ intent, route: redirectTo });
    };

    run();
  }, []);

  // Navigates on the tick after the card paints — see LoginVerifyTokenPage.
  useEffect(() => {
    if (!result) return;
    navigate(result.route, { replace: true });
  }, [result, navigate]);

  return (
    <AuthTransition
      status={result ? "success" : "pending"}
      busyLabel="Signing you in with LinkedIn"
    />
  );
};
